import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { makeStyles } from '@mui/styles'

import config from '~/src/App/config'
import Drawer from '~/src/Drawer'
import FacilityDashboardContext from '~/src/Facility/Dashboard/Context'
// import SatelliteView from '~/src/Facility/Dashboard/OutdoorBoard/SatelliteView'
import { useAllowed } from '~/src/Flags'
import FacilityJournal from '~/src/Journal'
import createLogger from '~/src/Lib/Logging'
import { idAndNameShape } from '~/src/Lib/PropTypes'
import { lazyWithRetries, useIsLoaded } from '~/src/Lib/Utils'
import { getQueryParam } from '~/src/Room/Dashboard/utils/query'
import JournalIcon from '~/src/UI/Icons/JournalNav'
import PageTitle from '~/src/UI/Shared/PageTitle'

const displayName = 'Facility/Dashboard'

const IndoorBoard = lazyWithRetries(() => import('~/src/Facility/Dashboard/IndoorBoard'))
const OutdoorBoard = lazyWithRetries(() => import('~/src/Facility/Dashboard/OutdoorBoard'))

const logger = createLogger(displayName)

const isTest = config.ENVIRONMENT === 'test'

const styles = theme => ({
  root: {
    height: '100%',
    maxHeight: 'calc(100vh - 64px)',
    display: 'grid',
    position: 'relative',
    gridTemplateAreas: [
      '"tabs"',
      '"map"',
    ].join(' '),
    gridTemplateRows: 'auto 1fr',
  },
  kabob: {
    gridArea: 'kabob',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  journal: {
    gridArea: 'journal',
    height: `calc(100% - ${theme.spacing(1.25)})`,
    overflow: 'hidden',
  },
})

const useStyles = makeStyles(styles, { name: displayName })

const getGreeting = me => {
  const time = new Date().getHours()
  let greeting = 'Hello'
  if (isTest || (time >= 0 && time < 12)) {
    greeting = 'Good Morning'
  } else if (time >= 12 && time < 18) {
    greeting = 'Good Afternoon'
  } else if (time >= 18 && time <= 23) {
    greeting = 'Good Evening'
  }

  return [greeting, me?.firstName].filter(Boolean).join(', ')
}

export const FacilityDashboardComponent = ({
  rooms,
  zones,
  me,
  currentFacility,
  breakpoint,
  currentHarvestList,
  cultivars,
  phases,
  doUpdateUrl,
  ...props
}) => {
  const {
    facilityStatus,
    harvestListIsLoading,
    queryObject,
    doFacilityStatusFetch,
    doFetchFacilityIrrigationSchedules,
    doUpdateQuery,
  } = useConnect(
    'selectFacilityStatus',
    'selectHarvestListIsLoading',
    'selectQueryObject',
    'doFacilityStatusFetch',
    'doFetchFacilityIrrigationSchedules',
    'doOpenDrawer',
    'doRenderDrawerTab',
    'doUpdateQuery',
  )

  const classes = useStyles(props)
  const [activeRoom, setActiveRoom] = useState(null)
  const [journalOpen, setJournalOpen] = useState(false)
  const showSatelliteView = getQueryParam(queryObject, 'satelliteView', false)
  const [hasIrrigation] = useAllowed({ actions: 'view_irrigation' })

  const facilityStatusFetchedRef = React.useRef(false)
  useEffect(() => {
    if (facilityStatus.loading || facilityStatusFetchedRef.current) return
    // TODO: make facility status its own async resource
    doFacilityStatusFetch()
    facilityStatusFetchedRef.current = true
  }, [doFacilityStatusFetch, facilityStatus.loading])

  const [isLoadingIrrigationSchedules, setIsLoadingIrrigationSchedules] = useState(hasIrrigation)
  useEffect(() => {
    if (!hasIrrigation) return
    const fetchSchedules = async () => {
      await doFetchFacilityIrrigationSchedules()
      setIsLoadingIrrigationSchedules(false)
    }

    fetchSchedules()
  }, [doFetchFacilityIrrigationSchedules, hasIrrigation])
  const dashboardLoaded = useIsLoaded(Boolean(
    facilityStatus?.loading || harvestListIsLoading || isLoadingIrrigationSchedules
  ))

  const { [activeRoom]: activeRoomData = null } = rooms
  const drawerTabs = useMemo(() => [
    {
      Icon: JournalIcon,
      key: 'journal',
      tooltip: 'Journal',
      content: (
        <FacilityJournal
          calendar
          activeRoom={activeRoomData}
          hidden={!journalOpen}
        />
      ),
      onOpen: () => setJournalOpen(true),
      onClose: () => setActiveRoom(null),
    },
  ], [activeRoomData, journalOpen])

  return showSatelliteView && currentFacility?.outdoor ? (
    // <SatelliteView doUpdateQuery={doUpdateQuery} />
    <span />
  ) : (
    <div data-testid="facility-dashboard" className={classes.root}>
      <Drawer
        tabs={drawerTabs}
        onClose={() => setActiveRoom(null)}
      />
      <PageTitle title={getGreeting(me)} />
      {currentFacility?.outdoor ? (
        <OutdoorBoard
          fields={rooms}
          zones={zones}
          currentHarvestList={currentHarvestList}
          cultivars={cultivars}
          phases={phases}
          queryObject={queryObject}
          dashboardLoaded={dashboardLoaded}
          facilityStatus={facilityStatus}
          doUpdateQuery={doUpdateQuery}
        />
      ) : (
        <IndoorBoard
          dashboardLoaded={dashboardLoaded}
          me={me}
          currentFacility={currentFacility}
          facilityStatus={facilityStatus}
          rooms={rooms}
          zones={zones}
          breakpoint={breakpoint}
          activeRoom={activeRoom || undefined}
          setActiveRoom={setActiveRoom}
          doUpdateUrl={doUpdateUrl}
        />
      )}
    </div>
  )
}

FacilityDashboardComponent.propTypes = {
  breakpoint: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']).isRequired,
  me: PropTypes.shape({
    firstName: PropTypes.string,
  }).isRequired,
  phases: PropTypes.objectOf(idAndNameShape).isRequired,
  rooms: PropTypes.objectOf(idAndNameShape).isRequired,
  cultivars: PropTypes.objectOf(idAndNameShape).isRequired,
  zones: PropTypes.objectOf(idAndNameShape).isRequired,
  currentHarvestList: PropTypes.arrayOf(idAndNameShape).isRequired,
  currentFacility: PropTypes.shape({
    floorplans: PropTypes.arrayOf(PropTypes.shape({
      floorplan: PropTypes.shape({
        groundsAst: PropTypes.shape({
          facility: PropTypes.shape({
            rooms: PropTypes.arrayOf(PropTypes.string),
            zones: PropTypes.arrayOf(PropTypes.string),
          })
        })
      })
    })),
    outdoor: PropTypes.bool,
  }).isRequired,
  doUpdateUrl: PropTypes.func.isRequired,
}

FacilityDashboardComponent.displayName = displayName

const Wrapped = React.memo(props => {
  const context = useContext(FacilityDashboardContext)
  return <FacilityDashboardComponent {...props} {...context} />
})

Wrapped.displayName = `FacilityDashboardContext(${displayName})`

export default Wrapped
